import { logSwallowed } from '@/lib/errors';
// ============================================================================
// CameraCaptureDialog.tsx — УНИВЕРСАЛЬНЫЙ захват с камеры (getUserMedia):
//   - видео-превью + снимок на canvas (JPEG, настраиваемое качество/размер);
//   - переключение камеры (facingMode user/environment);
//   - опциональный чекбокс «С координатами» (captureLocation — единая точка);
//   - используется везде: вкладка «Файлы», реквизит «Файл», ТЧ «Поле»,
//     будущий внутренний чат с интеграцией внешних мессенджеров.
// SSoT: снимок → File → вызывающий код (uploadFile/…).
// Ошибки явные (отказ камеры/геолокации), без тихих фоллбэков.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { Button } from '@/components/ui/button';
import { captureLocation, type GeoPoint } from '@/lib/geolocation';

export interface CameraCaptureResult {
  file: File;
  location: GeoPoint | null;
}

interface CameraCaptureDialogProps {
  open: boolean;
  onClose: () => void;
  /** Готовый снимок (File) + координаты (если запрошены и получены) */
  onCaptured: (result: CameraCaptureResult) => void;
  /** Префикс имени файла снимка (default «Снимок») */
  fileNamePrefix?: string;
  /** Качество JPEG 0..1 (default 0.92) */
  jpegQuality?: number;
  /** Максимальная ширина снимка в пикселях (default 1920) */
  maxWidth?: number;
  /** Показывать чекбокс «С координатами» (default false — чувствительные данные) */
  allowLocation?: boolean;
  /** Начальная камера: 'user' (селфи) | 'environment' (основная) */
  initialFacingMode?: 'user' | 'environment';
}

const DEFAULT_FILE_NAME = 'Снимок';
/** Сколько ждём реального изображения с камеры, прежде чем показать причину. */
const CAMERA_READY_TIMEOUT_MS = 9000;

/**
 * cameraErrorMessage — понятный текст по имени ошибки getUserMedia.
 * PWA/APK различаются причиной: запрет разрешения, отсутствие камеры,
 * занятость устройства, небезопасный адрес (getUserMedia недоступен).
 */
function cameraErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return 'Не удалось запустить камеру';
  const name = String((error as { name?: unknown }).name || '');
  const message = String((error as { message?: unknown }).message || '');
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return 'Доступ к камере запрещён. Разрешите камеру в настройках браузера или приложения и повторите.';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'Камера не найдена на этом устройстве.';
    case 'NotReadableError':
    case 'TrackStartError':
      return 'Камера занята другим приложением или недоступна. Закройте другие приложения и повторите.';
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return 'Камера не поддерживает выбранный режим. Попробуйте сменить камеру.';
    case 'SecurityError':
      return 'Браузер запретил доступ к камере. Откройте приложение по защищённому адресу (https) и повторите.';
    case 'AbortError':
      return 'Запуск камеры прерван. Повторите попытку.';
    default:
      return message || 'Не удалось запустить камеру';
  }
}

export function CameraCaptureDialog({
  open,
  onClose,
  onCaptured,
  fileNamePrefix = DEFAULT_FILE_NAME,
  jpegQuality = 0.92,
  maxWidth = 1920,
  allowLocation = false,
  initialFacingMode = 'environment',
}: CameraCaptureDialogProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>(initialFacingMode);
  const [starting, setStarting] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Превью снимка (до подтверждения): data URL + File
  const [shot, setShot] = useState<{ dataUrl: string; file: File } | null>(null);
  const [withLocation, setWithLocation] = useState(false);
  const [capturingLocation, setCapturingLocation] = useState(false);
  // Живое состояние для сторожа готовности камеры (без устаревших замыканий).
  const liveStateRef = useRef({ starting, videoReady, error, shot });
  liveStateRef.current = { starting, videoReady, error, shot };

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const startCamera = useCallback(async () => {
    setStarting(true);
    setVideoReady(false);
    setError(null);
    try {
      stopStream();
      // getUserMedia отсутствует в WebView без защищённого контекста
      // (APK/PWA) и в старых браузерах — объясняем причину, не роняем диалог.
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Камера недоступна: браузер не поддерживает съёмку или приложение открыто не по защищённому адресу (https).');
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch((e) => { logSwallowed(e, 'components/media/CameraCaptureDialog.tsx:78'); return undefined; });
      }
    } catch (e: unknown) {
      setError(cameraErrorMessage(e));
    } finally {
      setStarting(false);
    }
  }, [facingMode, stopStream]);

  useEffect(() => {
    if (open) {
      setShot(null);
      setError(null);
      setVideoReady(false);
      setWithLocation(false);
    } else {
      setVideoReady(false);
      stopStream();
    }
    return stopStream;
  }, [open, stopStream]);

  // Живой просмотр: камера работает ТОЛЬКО когда открыт диалог и нет снимка.
  // Запуск — в эффекте (а не в обработчике «Повторить»): к этому моменту
  // <video> уже смонтирован, поэтому поток гарантированно привязывается.
  useEffect(() => {
    if (!open || shot) {
      stopStream();
      return;
    }
    void startCamera();
    return stopStream;
  }, [open, shot, startCamera, stopStream]);

  // Сторож готовности: камера может «молчать» (занята другим приложением,
  // политика автовоспроизведения WebView) — не оставляем пользователя перед
  // вечным «Ожидание изображения»: по таймауту объясняем причину и освобождаем
  // устройство (кнопка «Повторить» поднимет камеру заново).
  useEffect(() => {
    if (!open || shot) return;
    const timer = window.setTimeout(() => {
      const live = liveStateRef.current;
      if (live.videoReady || live.error || live.shot) return;
      setError(live.starting
        ? 'Камера не запустилась за отведённое время. Проверьте разрешение на камеру и повторите.'
        : 'Камера не передаёт изображение. Закройте другие приложения, использующие камеру, и повторите.');
      setVideoReady(false);
      stopStream();
    }, CAMERA_READY_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [open, shot, starting, error, stopStream]);

  const takeShot = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !videoReady) {
      setError('Камера не готова — дождитесь изображения и повторите.');
      return;
    }
    // Масштаб снимка: не больше maxWidth (по ширине, пропорции сохраняются)
    let w = video.videoWidth;
    let h = video.videoHeight;
    if (w > maxWidth) {
      h = Math.round((h * maxWidth) / w);
      w = maxWidth;
    }
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setError('Не удалось создать снимок');
      return;
    }
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', jpegQuality);
    const blob = dataURLToBlob(dataUrl);
    if (!blob) {
      setError('Не удалось создать снимок');
      return;
    }
    const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
    const file = new File([blob], `${fileNamePrefix}_${stamp}.jpg`, { type: 'image/jpeg' });
    // Снимок готов — камеру освобождаем (на телефоне это экономит батарею
    // и не держит устройство занятым). «Повторить» поднимет её заново.
    stopStream();
    setVideoReady(false);
    setShot({ dataUrl, file });
  };

  /** Вернуться к съёмке: сбросить превью (камеру поднимет эффект живого просмотра). */
  const retakeShot = useCallback(() => {
    setShot(null);
    setError(null);
  }, []);

  const confirm = async () => {
    if (!shot) return;
    setError(null);
    let location: GeoPoint | null = null;
    if (withLocation) {
      setCapturingLocation(true);
      try {
        location = await captureLocation();
      } catch (e: unknown) {
        setError(e instanceof Error && e.message ? e.message : 'Не удалось определить координаты');
        return;
      } finally {
        setCapturingLocation(false);
      }
    }
    stopStream();
    onCaptured({ file: shot.file, location });
    setShot(null);
    setWithLocation(false);
  };

  return (
    <UniversalWindow open={open} onClose={onClose} title="Камера" blocking width="max-w-2xl">
      <div className="flex flex-col gap-3 p-4 min-h-[60vh]">
        {error && <div className="text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded px-2 py-1">{error}</div>}

        {shot ? (
          // Превью снимка: подтвердить / повторить
          <div className="flex-1 flex flex-col gap-3">
            <img src={shot.dataUrl} alt="Снимок" className="flex-1 min-h-0 object-contain rounded-md border bg-black" />
            <div className="flex items-center justify-center gap-2">
              <Button size="sm" variant="outline" onClick={retakeShot} disabled={capturingLocation}>
                Повторить
              </Button>
              {allowLocation && (
                <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
                  <input type="checkbox" checked={withLocation} onChange={(e) => setWithLocation(e.target.checked)} disabled={capturingLocation} />
                  С координатами
                  {capturingLocation && <span className="text-muted-foreground">…</span>}
                </label>
              )}
              <Button size="sm" onClick={() => void confirm()} disabled={capturingLocation}>
                {capturingLocation ? 'Определение координат…' : 'Использовать'}
              </Button>
            </div>
          </div>
        ) : (
          // Живой просмотр камеры
          <div className="flex-1 flex flex-col gap-3 min-h-0">
            <div className="relative flex-1 min-h-0 rounded-md border bg-black flex items-center justify-center overflow-hidden">
              <video
                ref={videoRef}
                playsInline
                muted
                autoPlay
                className="max-h-full max-w-full object-contain"
                onLoadedMetadata={() => setVideoReady(true)}
                onCanPlay={() => setVideoReady(true)}
              />
              {starting && (
                <span className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground bg-black/40">
                  Запуск камеры…
                </span>
              )}
              {!starting && !error && !videoReady && (
                <span className="absolute text-xs text-muted-foreground">Ожидание изображения с камеры…</span>
              )}
            </div>
            <div className="flex items-center justify-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setFacingMode((f) => (f === 'user' ? 'environment' : 'user'))} disabled={starting}>
                Сменить камеру
              </Button>
              {error && (
                <Button size="sm" variant="outline" onClick={() => void startCamera()} disabled={starting}>
                  Повторить
                </Button>
              )}
              <Button size="sm" onClick={takeShot} disabled={starting || !videoReady}>
                Снимок
              </Button>
            </div>
          </div>
        )}
      </div>
    </UniversalWindow>
  );
}

function dataURLToBlob(dataUrl: string): Blob | null {
  try {
    const idx = dataUrl.indexOf(',');
    const mime = dataUrl.slice(5, idx).split(';')[0];
    const bin = atob(dataUrl.slice(idx + 1));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  } catch {
    return null;
  }
}
